export interface AddOperation {
  readonly op: "add";
  readonly path: string;
  readonly value: any;
}

export interface RemoveOperation {
  readonly op: "remove";
  readonly path: string;
}

export interface ReplaceOperation {
  readonly op: "replace";
  readonly path: string;
  readonly value: any;
}

export const PathDelimeter = "/";

export type Operation = AddOperation | RemoveOperation | ReplaceOperation;

// Add object types here to be interpreted as opaque values
const defaultOpaqueObjectTypes: Record<string, boolean> = {
  Date: true,
  RegExp: true,
  String: true,
  Number: true,
  Float64Array: true, // Support for Float64Array
};

/**
 * An object differ that produces the RFC6902 JSON Patch objects. This is a heavily modified version of
 * the package `microdiff`. https://github.com/AsyncBanana/microdiff/blob/master/index.ts
 *
 * Main changes:
 * 1. Handle arbitrary object types (liked typed arrays) as opaque values.
 * 2. Produces RFC6902 Patches
 * 3. Cleans up some of the mutable object fields and cyclic JSON support
 *
 * The first change is important because `Doc` includes typed arrays and an off the shelf JSON diff doesn't
 * support that. But, keep in mind that, if you desire to use this for sending over the wire,
 * you should first call `unmarshalTypedArraysInObject`.
 *
 * @param obj The old object
 * @param newObj The new object
 * @param path The path to the object. This is assumed to be a RFC6901 JSON Pointer.
 * @param opaqueObjectTypes A map of object types to treat as opaque values
 * @returns An array of RFC6902 JSON Patch objects
 */
export default function diffObjects(
  obj: Record<string, any> | any[],
  newObj: Record<string, any> | any[],
  path: string = "",
  opaqueObjectTypes: Record<string, boolean> = defaultOpaqueObjectTypes,
): Operation[] {
  const diffs: Operation[] = [];

  // if the objects are the same, return an empty diff
  if (obj === newObj) {
    return diffs;
  }

  // For each key in the old object
  const keys = Array.isArray(obj) ? Object.keys(obj).reverse() : Object.keys(obj);
  for (const key of keys) {
    const oldVal = obj[key as keyof typeof obj];
    const keyEncoded = encodeJSONPath(key);

    // if the key is not present in the new object, add remove op
    if (newObj[key as keyof typeof newObj] === undefined && oldVal !== undefined) {
      diffs.push({
        op: "remove",
        path: path + PathDelimeter + keyEncoded,
      });
      continue;
    }

    const newVal = newObj[key as keyof typeof newObj];

    const areCompatibleObjects =
      typeof oldVal === "object" && typeof newVal === "object" && Array.isArray(oldVal) === Array.isArray(newVal);

    if (
      oldVal &&
      newVal &&
      areCompatibleObjects &&
      !opaqueObjectTypes[Object.getPrototypeOf(oldVal)?.constructor?.name]
    ) {
      // If the two values are objects and non-equal, recursively diff them
      diffs.push(...diffObjects(oldVal, newVal, path + PathDelimeter + keyEncoded));
    } else if (
      oldVal !== undefined &&
      newVal !== undefined &&
      oldVal !== newVal &&
      // treat NaN values as equivalent
      !(Number.isNaN(oldVal) && Number.isNaN(newVal)) &&
      !(areCompatibleObjects && (isNaN(oldVal) ? oldVal + "" === newVal + "" : +oldVal === +newVal))
    ) {
      // If the values are not equal, add a replace op
      diffs.push({
        op: "replace",
        path: path + PathDelimeter + keyEncoded,
        value: newVal,
      });
    }
  }

  // for each key in the new object, if it is not present in the old object, add an add op
  for (const key in newObj) {
    const newValue = newObj[key as keyof typeof newObj];
    const oldValue = obj[key as keyof typeof obj];

    if (newValue !== undefined && oldValue === undefined) {
      const keyEncoded = encodeJSONPath(key);
      diffs.push({
        op: "add",
        path: path + PathDelimeter + keyEncoded,
        value: newValue,
      });
    }
  }

  return diffs;
}

/**
 * Encode a path segment as an RFC6901 JSON Pointer
 */
function encodeJSONPath(path: string | number): string {
  return String(path).replace(/~/g, "~0").replace(/\//g, "~1");
}
